import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Archive,
  ExpandMore,
  History,
  Visibility,
  ToggleOn,
  ToggleOff
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import { formatDateWithTimezone, formatDateTimeWithTimezone, utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';

// Use same-origin API by default in browsers; allow override via REACT_APP_API_URL for local/dev tools
const API_URL = process.env.REACT_APP_API_URL || '/api';

const CompanyPolicyManagement = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [policies, setPolicies] = useState([]);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const tzLabel = getTimezoneLabel(displayTimezone);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyHistory, setPolicyHistory] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    policyType: '',
    category: ''
  });

  const [formData, setFormData] = useState({
    policyCode: '',
    policyName: '',
    policyDescription: '',
    policyType: 'OTHER',
    policyCategory: 'OPERATIONAL',
    appliesTo: 'ALL_CREW',
    appliedRoles: [],
    appliedCrewIds: [],
    policyRule: {},
    dgcaReference: '',
    effectiveDate: null,
    expiryDate: null
  });

  useEffect(() => {
    fetchPolicies();
  }, [filters]);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.policyType) params.append('policyType', filters.policyType);
      if (filters.category) params.append('category', filters.category);

      // Always fetch from main endpoint; filters control what is shown.
      const endpoint = '/ops/crew-duty-roster/company-policies';

      const response = await axios.get(`${API_URL}${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching company policies:', error);
      showToast('Failed to fetch company policies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPolicyHistory = async (policyId) => {
    try {
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/company-policies/${policyId}/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPolicyHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching policy history:', error);
      showToast('Failed to fetch policy history', 'error');
    }
  };

  const handleCreate = () => {
    setSelectedPolicy(null);
    setFormData({
      policyCode: '',
      policyName: '',
      policyDescription: '',
      policyType: 'OTHER',
      policyCategory: 'OPERATIONAL',
      appliesTo: 'ALL_CREW',
      appliedRoles: [],
      appliedCrewIds: [],
      policyRule: {},
      dgcaReference: '',
      effectiveDate: dayjs().add(1, 'day'),
      expiryDate: null
    });
    setDialogOpen(true);
  };

  const handleEdit = (policy) => {
    if (!['ACTIVE', 'DISABLED', 'DRAFT', 'EXPIRED'].includes(policy.status)) {
      showToast('Can only edit ACTIVE, DISABLED, DRAFT, or EXPIRED policies', 'warning');
      return;
    }
    setSelectedPolicy(policy);
    setFormData({
      policyCode: policy.policyCode,
      policyName: policy.policyName,
      policyDescription: policy.policyDescription,
      policyType: policy.policyType,
      policyCategory: policy.policyCategory,
      appliesTo: policy.appliesTo,
      appliedRoles: policy.appliedRoles || [],
      appliedCrewIds: policy.appliedCrewIds || [],
      policyRule: policy.policyRule || {},
      dgcaReference: policy.dgcaReference || '',
      effectiveDate: (() => {
        const d = policy.effectiveDate ? utcToDisplayDateAndTime(policy.effectiveDate, displayTimezone).date : '';
        return d ? dayjs(d) : null;
      })(),
      expiryDate: (() => {
        const d = policy.expiryDate ? utcToDisplayDateAndTime(policy.expiryDate, displayTimezone).date : '';
        return d ? dayjs(d) : null;
      })()
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const effectiveStr = formData.effectiveDate?.format?.('YYYY-MM-DD') || formData.effectiveDate;
      const expiryStr = formData.expiryDate?.format?.('YYYY-MM-DD') || formData.expiryDate;
      const effectiveDateISO = effectiveStr ? (displayDateTimeToUTC(effectiveStr, '00:00', displayTimezone) ?? undefined) : undefined;
      const expiryDateISO = expiryStr ? (displayDateTimeToUTC(expiryStr, '23:59', displayTimezone) ?? undefined) : undefined;
      const payload = {
        policyCode: formData.policyCode,
        policyName: formData.policyName,
        policyDescription: formData.policyDescription,
        policyType: formData.policyType,
        policyCategory: formData.policyCategory,
        appliesTo: formData.appliesTo,
        appliedRoles: formData.appliesTo === 'SPECIFIC_ROLES' ? formData.appliedRoles : undefined,
        appliedCrewIds: formData.appliesTo === 'SPECIFIC_CREW' ? formData.appliedCrewIds : undefined,
        policyRule: formData.policyRule,
        dgcaReference: formData.dgcaReference,
        effectiveDate: effectiveDateISO,
        expiryDate: expiryDateISO
      };

      if (selectedPolicy) {
        await axios.put(`${API_URL}/ops/crew-duty-roster/company-policies/${selectedPolicy._id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Company policy updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/ops/crew-duty-roster/company-policies`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Company policy created successfully', 'success');
      }

      setDialogOpen(false);
      fetchPolicies();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save company policy', 'error');
    }
  };

  // Approval workflow removed - policies are created as ACTIVE by SUPER_ADMIN

  const handleToggle = async (policyId, currentStatus) => {
    const action = currentStatus === 'ACTIVE' ? 'disable' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this policy?`)) {
      return;
    }

    try {
      await axios.put(`${API_URL}/ops/crew-duty-roster/company-policies/${policyId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast(`Policy ${action}d successfully`, 'success');
      fetchPolicies();
    } catch (error) {
      showToast(error.response?.data?.message || `Failed to ${action} policy`, 'error');
    }
  };

  const handleView = (policy) => {
    setSelectedPolicy(policy);
    setViewDialogOpen(true);
  };

  const handleArchive = async (policyId) => {
    if (!window.confirm('Are you sure you want to archive this policy? Archived policies cannot be reactivated.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/ops/crew-duty-roster/company-policies/${policyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Policy archived successfully', 'success');
      fetchPolicies();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to archive policy', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'DISABLED': return 'warning';
      case 'DRAFT': return 'info';
      case 'ARCHIVED': return 'default';
      case 'EXPIRED': return 'default';
      default: return 'default';
    }
  };

  const canEdit = (policy) => {
    return user?.role === ROLES.SUPER_ADMIN && ['ACTIVE', 'DISABLED', 'DRAFT', 'EXPIRED'].includes(policy.status);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Company Policy Management</Typography>
          {user?.role === ROLES.SUPER_ADMIN && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
            >
              Create Policy
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="DISABLED">Disabled</MenuItem>
                  <MenuItem value="ARCHIVED">Archived</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Policy Type</InputLabel>
                <Select
                  value={filters.policyType}
                  onChange={(e) => setFilters({ ...filters, policyType: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DUTY_TIME_LIMIT">Duty Time Limit</MenuItem>
                  <MenuItem value="REST_PERIOD">Rest Period</MenuItem>
                  <MenuItem value="CUMULATIVE_LIMIT">Cumulative Limit</MenuItem>
                  <MenuItem value="CREW_QUALIFICATION">Crew Qualification</MenuItem>
                  <MenuItem value="OPERATIONAL_RESTRICTION">Operational Restriction</MenuItem>
                  <MenuItem value="TRAINING">Training</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DGCA_COMPLIANCE">DGCA Compliance</MenuItem>
                  <MenuItem value="SAFETY">Safety</MenuItem>
                  <MenuItem value="OPERATIONAL">Operational</MenuItem>
                  <MenuItem value="QUALIFICATION">Qualification</MenuItem>
                  <MenuItem value="TRAINING">Training</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Policies Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Policy Code</TableCell>
                <TableCell>Policy Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Loading...</TableCell>
                </TableRow>
              ) : policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No policies found</TableCell>
                </TableRow>
              ) : (
                policies.map((policy) => (
                  <TableRow key={policy._id}>
                    <TableCell>{policy.policyCode}</TableCell>
                    <TableCell>{policy.policyName}</TableCell>
                    <TableCell>{policy.policyType.replace(/_/g, ' ')}</TableCell>
                    <TableCell>{policy.policyCategory.replace(/_/g, ' ')}</TableCell>
                    <TableCell>
                      <Chip
                        label={policy.status.replace(/_/g, ' ')}
                        color={getStatusColor(policy.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {displayTimezone ? formatDateWithTimezone(policy.effectiveDate, displayTimezone) : dayjs(policy.effectiveDate).format('MMM DD, YYYY')}
                    </TableCell>
                    <TableCell>{policy.version || 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(policy)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View History">
                          <IconButton size="small" onClick={() => fetchPolicyHistory(policy._id)}>
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEdit(policy) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(policy)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {['ACTIVE', 'DISABLED'].includes(policy.status) && user?.role === ROLES.SUPER_ADMIN && (
                          <Tooltip title={policy.status === 'ACTIVE' ? 'Disable' : 'Activate'}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleToggle(policy._id, policy.status)}
                              color={policy.status === 'ACTIVE' ? 'warning' : 'success'}
                            >
                              {policy.status === 'ACTIVE' ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {['ACTIVE', 'DISABLED', 'DRAFT', 'EXPIRED'].includes(policy.status) && canEdit(policy) && (
                          <Tooltip title="Archive">
                            <IconButton size="small" onClick={() => handleArchive(policy._id)}>
                              <Archive fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{selectedPolicy ? 'Edit Company Policy' : 'Create Company Policy'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Policy Code"
                  value={formData.policyCode}
                  onChange={(e) => setFormData({ ...formData, policyCode: e.target.value.toUpperCase() })}
                  required
                  disabled={!!selectedPolicy}
                  helperText="Unique code for the policy (e.g., CP-DUTY-001)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Policy Type</InputLabel>
                  <Select
                    value={formData.policyType}
                    onChange={(e) => setFormData({ ...formData, policyType: e.target.value })}
                    label="Policy Type"
                  >
                    <MenuItem value="DUTY_TIME_LIMIT">Duty Time Limit</MenuItem>
                    <MenuItem value="REST_PERIOD">Rest Period</MenuItem>
                    <MenuItem value="CUMULATIVE_LIMIT">Cumulative Limit</MenuItem>
                    <MenuItem value="CREW_QUALIFICATION">Crew Qualification</MenuItem>
                    <MenuItem value="OPERATIONAL_RESTRICTION">Operational Restriction</MenuItem>
                    <MenuItem value="TRAINING">Training</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Policy Name"
                  value={formData.policyName}
                  onChange={(e) => setFormData({ ...formData, policyName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Policy Description"
                  value={formData.policyDescription}
                  onChange={(e) => setFormData({ ...formData, policyDescription: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.policyCategory}
                    onChange={(e) => setFormData({ ...formData, policyCategory: e.target.value })}
                    label="Category"
                  >
                    <MenuItem value="DGCA_COMPLIANCE">DGCA Compliance</MenuItem>
                    <MenuItem value="SAFETY">Safety</MenuItem>
                    <MenuItem value="OPERATIONAL">Operational</MenuItem>
                    <MenuItem value="QUALIFICATION">Qualification</MenuItem>
                    <MenuItem value="TRAINING">Training</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Applies To</InputLabel>
                  <Select
                    value={formData.appliesTo}
                    onChange={(e) => setFormData({ ...formData, appliesTo: e.target.value })}
                    label="Applies To"
                  >
                    <MenuItem value="ALL_CREW">All Crew</MenuItem>
                    <MenuItem value="SPECIFIC_ROLES">Specific Roles</MenuItem>
                    <MenuItem value="SPECIFIC_CREW">Specific Crew</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="DGCA Reference"
                  value={formData.dgcaReference}
                  onChange={(e) => setFormData({ ...formData, dgcaReference: e.target.value })}
                  placeholder="e.g., CAR Section 7, Series J"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tzLabel ? `Effective Date (${tzLabel})` : 'Effective Date'}
                  value={formData.effectiveDate}
                  onChange={(newValue) => setFormData({ ...formData, effectiveDate: newValue })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={tzLabel ? `Expiry Date (Optional) (${tzLabel})` : 'Expiry Date (Optional)'}
                  value={formData.expiryDate}
                  onChange={(newValue) => setFormData({ ...formData, expiryDate: newValue })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  Policy rules must be defined as JSON.
                  {(formData.policyType === 'TRAINING' || formData.policyCategory === 'TRAINING') ? (
                    <> For multi-day training hours: {"{"} "multiDayTrainingHoursPerDay": 10 {"}"} (one day = 10 hours when training spans multiple days).</>
                  ) : (
                    <> Example: {"{"} "maxDailyDutyHours": 9, "warningThreshold": 7 {"}"}</>
                  )}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Policy Rule (JSON)"
                  value={JSON.stringify(formData.policyRule, null, 2)}
                  onChange={(e) => {
                    try {
                      const rule = JSON.parse(e.target.value);
                      setFormData({ ...formData, policyRule: rule });
                    } catch (err) {
                      // Invalid JSON, keep as is
                    }
                  }}
                  helperText="Enter policy rule as JSON object"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {selectedPolicy ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Policy Version History</DialogTitle>
          <DialogContent>
            {policyHistory.map((entry, idx) => {
              const before = entry.before || {};
              const after = entry.after || {};
              const versionNumber = after.version || before.version || (policyHistory.length - idx);
              const status = after.status || before.status || 'UNKNOWN';
              const actor = entry.actorId;
              return (
                <Accordion key={entry._id || idx}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                      <Box>
                        <Typography>Version {versionNumber} — {entry.action}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {displayTimezone ? formatDateTimeWithTimezone(entry.createdAt, null, displayTimezone) : dayjs(entry.createdAt).format('MMM DD, YYYY HH:mm')} {actor && `by ${actor.firstName} ${actor.lastName}`}
                        </Typography>
                      </Box>
                      <Chip
                        label={status}
                        color={getStatusColor(status)}
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2"><strong>Name:</strong> {after.policyName || before.policyName}</Typography>
                    <Typography variant="body2"><strong>Description:</strong> {after.policyDescription || before.policyDescription}</Typography>
                    {(after.effectiveDate || before.effectiveDate) && (
                      <Typography variant="body2">
                        <strong>Effective Date:</strong>{' '}
                        {displayTimezone ? formatDateWithTimezone(after.effectiveDate || before.effectiveDate, displayTimezone) : dayjs(after.effectiveDate || before.effectiveDate).format('MMM DD, YYYY')}
                      </Typography>
                    )}
                    {(after.status || before.status) && (
                      <Typography variant="body2">
                        <strong>Status:</strong> {(after.status || before.status).replace(/_/g, ' ')}
                      </Typography>
                    )}
                    {entry.action && (
                      <Typography variant="body2">
                        <strong>Action:</strong> {entry.action}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* View Policy Dialog (Read-only) */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Policy Details {selectedPolicy?.status === 'ARCHIVED' && <Chip label="Archived" size="small" color="default" sx={{ ml: 1 }} />}
          </DialogTitle>
          <DialogContent>
            {selectedPolicy && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Policy Code"
                    value={selectedPolicy.policyCode}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={selectedPolicy.status.replace(/_/g, ' ')}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Policy Type"
                    value={selectedPolicy.policyType.replace(/_/g, ' ')}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={selectedPolicy.policyCategory.replace(/_/g, ' ')}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Policy Name"
                    value={selectedPolicy.policyName}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Policy Description"
                    value={selectedPolicy.policyDescription}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Applies To"
                    value={selectedPolicy.appliesTo.replace(/_/g, ' ')}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Version"
                    value={selectedPolicy.version || 1}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Effective Date"
                    value={displayTimezone ? formatDateWithTimezone(selectedPolicy.effectiveDate, displayTimezone) : dayjs(selectedPolicy.effectiveDate).format('MMM DD, YYYY')}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Expiry Date"
                    value={selectedPolicy.expiryDate ? (displayTimezone ? formatDateWithTimezone(selectedPolicy.expiryDate, displayTimezone) : dayjs(selectedPolicy.expiryDate).format('MMM DD, YYYY')) : 'N/A'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {selectedPolicy.dgcaReference && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="DGCA Reference"
                      value={selectedPolicy.dgcaReference}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Policy Rule (JSON)"
                    value={JSON.stringify(selectedPolicy.policyRule, null, 2)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {selectedPolicy.archivedBy && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      This policy was archived on {displayTimezone ? formatDateWithTimezone(selectedPolicy.archivedAt, displayTimezone) : dayjs(selectedPolicy.archivedAt).format('MMM DD, YYYY')}. 
                      Archived policies cannot be reactivated.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CompanyPolicyManagement;

